import React, { FC, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Map as MapboxMap, Marker, GeolocateControl, FullscreenControl, Source, Layer, MapRef } from './map/LazyMapbox';
import { MAPBOX_ACCESS_TOKEN } from '../lib/map/token';
// Mapbox CSS is now loaded dynamically in LazyMapbox
import { Stop, Day, Leg, LegTravelModeEnum } from "@city-sage/core-client";
import { MapPin, Clock } from "lucide-react";
import { getDayColor } from '../lib/constants/dayColors';
import { cn } from '@city-sage/aurum-avis-labs-components';
import { motion } from 'framer-motion';
import { getTravelModeIcon, getTravelModeColor, formatDuration, formatDistance } from '../lib/travelModeUtils';
import { StopPreviewSheet } from './main-map/StopPreviewSheet';

interface TripMapProps {
  stops: Stop[];
  days: Day[];
  legs: Leg[];
  onStopClick?: (stop: Stop) => void;
  className?: string;
  selectedStop?: Stop | null;
  onMapReady?: (mapRef: React.RefObject<MapRef>) => void;
  interactive?: boolean;
  hideLegs?: boolean;
  itineraryId?: string;
  enablePreviewSheet?: boolean;
  showFullscreenControl?: boolean;
  hideDayBadge?: boolean;
}

export const TripMap: FC<TripMapProps> = ({ 
  stops,
  days,
  legs,
  onStopClick,
  className,
  selectedStop,
  onMapReady,
  interactive = true,
  hideLegs = false,
  itineraryId,
  enablePreviewSheet = false,
  showFullscreenControl = false,
  hideDayBadge = false
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [previewStop, setPreviewStop] = useState<Stop | null>(null);
  const [isPreviewSheetOpen, setIsPreviewSheetOpen] = useState(false);
  
  // Create a map of stop ID to day number for color consistency
  // This ensures stops with the same day number always have the same color
  const stopToDayNumber = useMemo(() => {
    const stopToDayMap: Map<string, number> = new Map();
    days.forEach(day => {
      day.stopIds?.forEach(stopId => {
        stopToDayMap.set(stopId, day.number);
      });
    });
    // Also check stops directly if they have dayIds
    stops.forEach(stop => {
      if (stop.dayIds && stop.dayIds.length > 0) {
        const day = days.find(d => stop.dayIds?.includes(d.id));
        if (day) {
          stopToDayMap.set(stop.id, day.number);
        }
      }
    });
    return stopToDayMap;
  }, [days, stops]);


  // Helper function to calculate midpoint of a route
  const calculateMidpoint = (coordinates: number[][]) => {
    if (coordinates.length === 0) return null;
    if (coordinates.length === 1) return coordinates[0];
    if (coordinates.length === 2) {
      // Simple midpoint for straight lines
      const [lng1, lat1] = coordinates[0];
      const [lng2, lat2] = coordinates[1];
      return [(lng1 + lng2) / 2, (lat1 + lat2) / 2];
    }
    
    // For complex routes, find the coordinate closest to the middle index
    const midIndex = Math.floor(coordinates.length / 2);
    return coordinates[midIndex];
  };

  // Helper function to create straight lines for missing legs between consecutive stops
  const createMissingLegs = (stops: Stop[], legs: Leg[], stopToDayMap: Map<string, number>) => {
    if (!stops || stops.length < 2) return [];
    
    const missingLegs: Array<Partial<Leg> & { id: string; fromStopId: string; toStopId: string; travelMode: LegTravelModeEnum }> = [];
    
    // Group stops by day
    const stopsByDay = new Map<number, Stop[]>();
    stops.forEach(stop => {
      const dayNumber = stopToDayMap.get(stop.id);
      if (dayNumber !== undefined) {
        if (!stopsByDay.has(dayNumber)) {
          stopsByDay.set(dayNumber, []);
        }
        stopsByDay.get(dayNumber)!.push(stop);
      }
    });
    
    // For each day, check consecutive stops within that day
    stopsByDay.forEach((dayStops, dayNumber) => {
      // Sort stops by index within the day
      const sortedDayStops = [...dayStops].sort((a, b) => (a.index || 0) - (b.index || 0));
      
      // Check each consecutive pair of stops within the same day
      for (let i = 0; i < sortedDayStops.length - 1; i++) {
        const currentStop = sortedDayStops[i];
        const nextStop = sortedDayStops[i + 1];
        
        // Check if there's a leg connecting these stops
        const existingLeg = legs?.find(leg => 
          leg.fromStopId === currentStop.id && leg.toStopId === nextStop.id
        );
        
        if (!existingLeg && currentStop.place?.coordinates && nextStop.place?.coordinates) {
          // Create a virtual leg for missing connection within the same day
          const virtualLeg: Partial<Leg> & { id: string; fromStopId: string; toStopId: string; travelMode: LegTravelModeEnum } = {
            id: `virtual-${currentStop.id}-${nextStop.id}`,
            fromStopId: currentStop.id,
            toStopId: nextStop.id,
            travelMode: LegTravelModeEnum.Walking, // Default to walking for missing connections
            travelTimeMinutes: undefined,
            distanceMeters: undefined,
            route: undefined,
            notes: undefined
          };
          
          missingLegs.push(virtualLeg);
        }
      }
    });
    
    return missingLegs;
  };

  // Helper function to create straight line leg
  const createStraightLineLeg = (leg: Leg | (Partial<Leg> & { id: string; fromStopId: string; toStopId: string; travelMode: LegTravelModeEnum })) => {
    // Find the from and to stops in our stops data
    const fromStop = stops.find(stop => stop.id === leg.fromStopId);
    const toStop = stops.find(stop => stop.id === leg.toStopId);
    
    if (!fromStop?.place?.coordinates || !toStop?.place?.coordinates) {
      return null;
    }
    
    const fromCoords = [fromStop.place.coordinates.longitude, fromStop.place.coordinates.latitude];
    const toCoords = [toStop.place.coordinates.longitude, toStop.place.coordinates.latitude];
    
    // Create a simple LineString geometry with just two points
    const straightLineGeometry = {
      type: 'LineString' as const,
      coordinates: [fromCoords, toCoords]
    };
    
    const isVirtual = leg.id.startsWith('virtual-');
    
    return {
      legId: leg.id,
      geoJson: {
        type: 'Feature' as const,
        properties: {
          travelMode: leg.travelMode,
          travelTime: leg.travelTimeMinutes || null,
          distance: leg.distanceMeters || null,
          notes: leg.notes || null,
          isStraightLine: true, // Flag to indicate this is a fallback
          isVirtual: isVirtual // Flag to indicate this is a virtual leg for missing connection
        },
        geometry: straightLineGeometry
      },
      travelMode: leg.travelMode,
      color: getTravelModeColor(leg.travelMode)
    };
  };

  // Create GeoJSON for leg routes (actual routes with travel data)
  const legRoutes = useMemo(() => {
    // Create virtual legs for missing connections between consecutive stops within the same day
    const virtualLegs = createMissingLegs(stops, legs, stopToDayNumber);
    
    // Combine actual legs with virtual legs
    const allLegs = [...legs, ...virtualLegs];
    
    if (allLegs.length === 0) return [];
    
    const routes = allLegs.map(leg => {
      // If leg has route data (GeoJSON from Mapbox), use it
      if (leg.route) {
        try {
          const routeGeometry = typeof leg.route === 'string' ? JSON.parse(leg.route) : leg.route;
          
          // Validate that we have a proper GeoJSON geometry
          if (!routeGeometry || !routeGeometry.type || !routeGeometry.coordinates) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[TripMap] Invalid route geometry for leg:', leg.id, routeGeometry);
            }
            return createStraightLineLeg(leg);
          }
          
          // The routeGeometry should be a GeoJSON geometry object
          // We need to create a proper Feature object
          const geoJsonFeature = {
            type: 'Feature' as const,
            properties: {
              travelMode: leg.travelMode,
              travelTime: leg.travelTimeMinutes || null,
              distance: leg.distanceMeters || null,
              notes: leg.notes || null,
              isStraightLine: false, // This is an actual route with geometry
              isVirtual: false // This is a real leg
            },
            geometry: routeGeometry
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[TripMap] Created route with geometry for leg:', {
              legId: leg.id,
              travelMode: leg.travelMode,
              coordinates: routeGeometry.coordinates?.length || 0,
              hasRoute: true
            });
          }
          
          return {
            legId: leg.id,
            geoJson: geoJsonFeature,
            travelMode: leg.travelMode,
            color: getTravelModeColor(leg.travelMode)
          };
        } catch (error) {
          // Fallback to straight line if route parsing fails
          if (process.env.NODE_ENV === 'development') {
            console.error('[TripMap] Error parsing route for leg:', leg.id, error);
          }
          return createStraightLineLeg(leg);
        }
      } else {
        // Fallback to straight line between stops
        if (process.env.NODE_ENV === 'development') {
          console.log('[TripMap] No route data for leg, creating straight line:', {
            legId: leg.id,
            travelMode: leg.travelMode
          });
        }
        return createStraightLineLeg(leg);
      }
    }).filter((route): route is NonNullable<typeof route> => route !== null);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[TripMap] Total leg routes generated:', routes.length, {
        byTravelMode: routes.reduce((acc, r) => {
          acc[r.travelMode] = (acc[r.travelMode] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      });
    }
    
    return routes;
  }, [legs, stops, stopToDayNumber]);

  // Calculate bounds
  const bounds = useMemo(() => {
    if (stops.length === 0) return null;
    
    const lngs = stops.map(stop => stop.place.coordinates!.longitude!).filter(Boolean);
    const lats = stops.map(stop => stop.place.coordinates!.latitude!).filter(Boolean);
    
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  }, [stops]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (stops.length === 0) {
      return {
        longitude: 0,
        latitude: 20,
        zoom: 1
      };
    }
    
    if (stops.length === 1) {
      return {
        longitude: stops[0].place.coordinates!.longitude!,
        latitude: stops[0].place.coordinates!.latitude!,
        zoom: 14
      };
    }
    
    if (bounds) {
      return {
        longitude: (bounds.minLng + bounds.maxLng) / 2,
        latitude: (bounds.minLat + bounds.maxLat) / 2,
        zoom: 10
      };
    }
    
    return {
      longitude: 0,
      latitude: 20,
      zoom: 1
    };
  }, [stops, bounds]);

  // Expose map ref to parent
  useEffect(() => {
    if (mapRef.current && onMapReady) {
      onMapReady(mapRef);
    }
  }, [onMapReady]);

  // Fit bounds when map loads (only on initial load)
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (mapRef.current && bounds && stops.length > 1 && !hasInitialized) {
      const map = mapRef.current.getMap();
      
      setTimeout(() => {
        map.fitBounds(
          [
            [bounds.minLng, bounds.minLat],
            [bounds.maxLng, bounds.maxLat]
          ],
          {
            padding: { top: 40, bottom: 40, left: 40, right: 40 },
            duration: 1000,
            maxZoom: 16
          }
        );
        setHasInitialized(true);
      }, 100);
    } else if (mapRef.current && stops.length === 1 && !hasInitialized) {
      const map = mapRef.current.getMap();
      const stop = stops[0];
      
      setTimeout(() => {
        map.flyTo({
          center: [stop.place.coordinates!.longitude, stop.place.coordinates!.latitude],
          zoom: 14,
          duration: 1000,
          essential: true
        });
        setHasInitialized(true);
      }, 100);
    }
  }, [bounds, stops, hasInitialized]);

  // Fly to selected stop when it changes (only on explicit selection, not scroll)
  useEffect(() => {
    if (selectedStop && mapRef.current && hasInitialized) {
      const map = mapRef.current.getMap();
      const stop = selectedStop;
      
      if (stop.place.coordinates?.latitude && stop.place.coordinates?.longitude) {
        map.flyTo({
          center: [stop.place.coordinates.longitude, stop.place.coordinates.latitude],
          zoom: 15,
          duration: 800,
          essential: true
        });
      }
    }
  }, [selectedStop, hasInitialized]);

  // Handle map move/zoom events using react-map-gl callbacks
  const handleMove = useCallback((evt: { viewState: { zoom: number } }) => {
    if (evt?.viewState?.zoom !== undefined) {
      setCurrentZoom(evt.viewState.zoom);
    }
  }, []);

  // Handle map load to set initial zoom
  const handleLoad = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      setCurrentZoom(map.getZoom());
    }
  }, []);

  // Handle stop click - show preview sheet if enabled, otherwise call onStopClick
  const handleStopClick = (stop: Stop) => {
    if (enablePreviewSheet) {
      setPreviewStop(stop);
      setIsPreviewSheetOpen(true);
    } else {
      onStopClick?.(stop);
    }
  };

  // Find the day for the preview stop
  const previewStopDay = useMemo(() => {
    if (!previewStop || !days.length) return undefined;
    return days.find(day => 
      day.stopIds?.includes(previewStop.id) || 
      previewStop.dayIds?.includes(day.id)
    );
  }, [previewStop, days]);

  if (stops.length === 0) {
    return (
      <div className={cn("aspect-video bg-gray-100 rounded-lg flex items-center justify-center", className)}>
        <div className="text-center text-gray-500">
          <MapPin className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">Add stops to see map preview</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      <MapboxMap
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        initialViewState={initialViewState}
        onMove={handleMove}
        onLoad={handleLoad}
        interactive={interactive}
        dragPan={interactive}
        dragRotate={interactive}
        scrollZoom={interactive}
        doubleClickZoom={interactive}
        touchZoomRotate={interactive}
        touchPitch={interactive}
        keyboard={interactive}
      >
        {/* Render leg routes */}
        {!hideLegs && legRoutes.map((route) => {
          const isVirtual = route.geoJson.properties.isVirtual;
          const isStraightLine = route.geoJson.properties.isStraightLine;
          
          // Virtual legs are always straight lines (placeholders for missing connections)
          // isStraightLine without isVirtual means a real leg without route geometry
          // Neither flag means a real leg with calculated route geometry
          
          // Check if this is a walking route (handle both string and enum values)
          const travelModeStr = String(route.travelMode).toUpperCase();
          const isWalking = travelModeStr === 'WALKING' || route.travelMode === LegTravelModeEnum.Walking;
          
          // Determine dash array pattern (only set when we want a dashed line)
          let dashArray: number[] | undefined;
          if (isVirtual) {
            dashArray = [2, 6]; // Dotted line for virtual placeholder connections
          } else if (isStraightLine) {
            dashArray = [4, 4]; // Dashed line for real legs without route geometry
          }
          // For walking/car/cycling with route geometry, dashArray remains undefined (solid line)
          
          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('[TripMap] Rendering leg route:', {
              legId: route.legId,
              travelMode: route.travelMode,
              isWalking,
              isVirtual,
              isStraightLine,
              hasRouteGeometry: !isStraightLine && !isVirtual,
              dashArray,
              color: route.color,
              coordinates: route.geoJson.geometry.coordinates?.length || 0
            });
          }
          
          // Build paint object conditionally
          const paint: Record<string, any> = {
            'line-color': route.color,
            'line-width': isVirtual ? 2 : isWalking ? 3 : 4,
            'line-opacity': isVirtual ? 0.75 : isStraightLine ? 0.5 : 0.8,
          };
          
          // Only include line-dasharray if we have a dash pattern
          if (dashArray) {
            paint['line-dasharray'] = dashArray;
          }
          
          return (
            <Source key={route.legId} type="geojson" data={route.geoJson}>
              <Layer
                type="line"
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round',
                }}
                paint={paint}
              />
            </Source>
          );
        })}

        {/* Render leg metadata at midpoints */}
        {!hideLegs && currentZoom >= 12 && legRoutes.map((route) => {
          const coordinates = route.geoJson.geometry.coordinates;
          const midpoint = calculateMidpoint(coordinates);
          
          if (!midpoint) return null;
          
          const IconComponent = getTravelModeIcon(route.travelMode);
          const duration = formatDuration(route.geoJson.properties.travelTime);
          const distance = formatDistance(route.geoJson.properties.distance);
          const isStraightLine = route.geoJson.properties.isStraightLine;
          const isVirtual = route.geoJson.properties.isVirtual;
          
          // Don't show metadata for virtual legs or if we have no duration/distance info
          if (isVirtual || (!duration && !distance)) return null;
          
          // Show full badge when zoomed in (>= 14), just indicator when zoomed out (12-13)
          const showFullBadge = currentZoom >= 14;
          
          return (
            <Marker
              key={`${route.legId}-metadata`}
              longitude={midpoint[0]}
              latitude={midpoint[1]}
              anchor="center"
            >
              {showFullBadge ? (
                <motion.div
                  className={cn(
                    "bg-white/95 backdrop-blur-sm rounded-md shadow-md border px-1.5 py-0.5 flex items-center gap-1 text-[9px] font-normal pointer-events-none"
                  )}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  style={{ 
                    color: route.color,
                    borderColor: `${route.color}40`,
                    zIndex: 1
                  }}
                >
                  <IconComponent className="h-2.5 w-2.5" style={{ color: route.color }} />
                  <div className="flex items-center gap-0.5 text-gray-700">
                    {duration && (
                      <span className="flex items-center gap-0.5">
                        <Clock className="h-2 w-2" />
                        ~{duration}
                      </span>
                    )}
                    {duration && distance && <span>•</span>}
                    {distance && <span>{distance}</span>}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  className={cn(
                    "bg-white/95 backdrop-blur-sm rounded-full shadow-md border flex items-center justify-center pointer-events-none"
                  )}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  style={{ 
                    width: '16px',
                    height: '16px',
                    borderColor: `${route.color}40`,
                    zIndex: 1
                  }}
                >
                  <IconComponent className="h-2 w-2" style={{ color: route.color }} />
                </motion.div>
              )}
            </Marker>
          );
        })}

        {/* Render stop markers */}
        {stops.map((stop) => {
          const dayNumber = stopToDayNumber.get(stop.id);
          const dayColor = dayNumber ? getDayColor(dayNumber).hex : '#6366f1'; // fallback color
          const hasImage = !!stop.imageUrl;
          const showTitle = currentZoom >= 11 && stop.title;
          
          return (
            <Marker
              key={stop.id}
              longitude={stop.place.coordinates?.longitude}
              latitude={stop.place.coordinates?.latitude}
              anchor={showTitle ? "bottom" : "center"}
            >
              <motion.div 
                className="flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {hasImage ? (
                  <div className="relative">
                    <div
                      className="w-14 h-14 rounded-full border-2 border-white shadow-lg overflow-hidden hover:scale-110 transition-transform duration-200 cursor-pointer"
                      title={stop.title || `Stop ${stop.index}`}
                      onClick={() => handleStopClick(stop)}
                    >
                      <img
                        src={stop.imageUrl}
                        alt={stop.title || `Stop ${stop.index}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Day indicator badge positioned outside image container to avoid clipping */}
                    {!hideDayBadge && (
                      <div
                        className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-[9px] font-bold text-white z-10"
                        style={{ backgroundColor: dayColor }}
                      >
                        {stop.index}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer"
                    style={{ backgroundColor: dayColor }}
                    title={stop.title || `Stop ${stop.index}`}
                    onClick={() => handleStopClick(stop)}
                  >
                    <span className="text-xs font-bold text-white">{stop.index}</span>
                  </div>
                )}
                {/* Title label shown at higher zoom levels */}
                {showTitle && (
                  <motion.div
                    className="mt-1.5 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-md shadow-md border border-white/20 text-[10px] font-medium text-gray-800 whitespace-nowrap pointer-events-none"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {stop.title}
                  </motion.div>
                )}
              </motion.div>
            </Marker>
          );
        })}

        {/* Geolocation Control */}
        <GeolocateControl
          position="top-right"
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation={true}
          showAccuracyCircle={false}
        />

        {/* Fullscreen Control */}
        {showFullscreenControl && (
          <FullscreenControl position="top-right" />
        )}
      </MapboxMap>

      {/* Stop Preview Sheet */}
      {enablePreviewSheet && (
        <StopPreviewSheet
          stop={previewStop}
          itineraryId={itineraryId}
          currentDay={previewStopDay}
          isOpen={isPreviewSheetOpen}
          onClose={() => {
            setIsPreviewSheetOpen(false);
            setPreviewStop(null);
          }}
        />
      )}
    </div>
  );
}; 